<template>
<v-form>
  <v-card>
    <v-card-title class='subheading' >
      <v-btn @click='get' :loading='requests.configure.loading' icon small >
        <v-icon>replay</v-icon>
      </v-btn>
      Configure Mailchimp
      <v-spacer></v-spacer>
      <v-btn href='https://mailchimp.com'
        color='success' target='_blank' small >
        Get a Mailchimp account
        <v-icon small class='pl-2' >open_in_new</v-icon>
      </v-btn>
    </v-card-title>
    <v-card-text>
      <v-alert :value='true' type='info' outline outlined >
        You can get your mailchimp API Key from:<br/>
        <code>Click on your name (top right) -> profile -> extras -> API keys</code><br/>
        Or click this link: <a href='https://admin.mailchimp.com/account/api/' target='_blank' >
        https://admin.mailchimp.com/account/api/
        <v-icon small >open_in_new</v-icon>
        </a><br/>
        If you don't already have a key, you will need to create one
      </v-alert>

      <v-layout>
        <v-flex xs10>
          <v-text-field
            v-model='token'
            @keyup.enter.prevent="configure"
            label='Mailchimp API key' ></v-text-field>
        </v-flex>
        <v-spacer></v-spacer>
        <v-flex xs2 class='mt-2' >
          <v-btn :disabled='!token' block @click='configure' :loading='requests.configure.loading' >Save token</v-btn>
        </v-flex>
      </v-layout>

      <v-layout v-if='savedToken' >
        <v-flex xs10 >
          <v-select
            v-model='listId'
            @click:prepend='getLists'
            :items='lists'
            :loading='requests.lists.loading'
            item-text='name'
            item-value='id'
            prepend-icon='replay' label='Choose a list to connect to AppointmentGuru' >
          </v-select>
        </v-flex>
        <v-spacer></v-spacer>
        <v-flex xs2 class='mt-2' >
          <v-btn :loading='requests.list.loading' :disabled='!listId' block @click='setList' >Set list</v-btn>
        </v-flex>
      </v-layout>

    </v-card-text>
  </v-card>
  <v-card class='mt-3' v-if='savedToken && savedListId' >
    <v-card-title >
      Sync contacts from AppointmentGuru to the
      <strong class='mx-1'>{{savedList.name}}</strong>
      mailing list
    </v-card-title>
    <v-card-text >
      <v-layout >
        <v-flex xs12 sm6>
          <!-- <v-alert outline outlined type='info' :value='true' >
            <strong>Note</strong>
            The most contacts we can sync at one time is 1000.
            If you have more than 1000 contacts, we will sync 1000 recently active contacts
          </v-alert> -->
          <v-dialog ref="dialog" v-model="datepicker" width="290px" >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field clearable persistent-hint
                v-model='syncSince'
                label='Only sync recent contacts'
                hint='Only sync contacts with appointments since this date. Leave blank to sync all contacts'
                readonly
                v-bind="attrs"
                v-on="on" >
              </v-text-field>
            </template>
            <v-date-picker v-model="syncSince" scrollable>
              <!-- <v-spacer></v-spacer>
              <v-btn text color="primary" @click="datepicker = false">Cancel</v-btn>
              <v-btn text color="primary" @click="$refs.dialog.save(date)">OK</v-btn> -->
            </v-date-picker>
          </v-dialog>
          <v-btn @click='performSync' :loading='requests.sync.loading' color='primary' block >Start sync</v-btn>
        </v-flex>
        <v-flex xs12 sm6 v-if='syncBatchId' >
          <!-- <v-progress-linear v-if='requests.sync.loading || requests.syncstatus.loading' class='pa-0 ma-0 mt-1' height='2' indeterminate ></v-progress-linear> -->

          <v-subheader class='pl-4 overline'>
            Results
            <v-spacer></v-spacer>
            <v-chip v-if='syncStatus.status === "finished"' small dark color='success' >{{syncStatus.status}}</v-chip>
            <v-chip v-else small >
              <v-avatar left >
                <v-progress-circular :size='20' :width='2' indeterminate ></v-progress-circular>
              </v-avatar>
              {{syncStatus.status}}
            </v-chip>
          </v-subheader>
          <blockquote class='blockquote' >
            <div><strong>Total imports</strong> {{syncStatus.total_operations}}</div>
            <div><strong>Completed imports</strong> {{syncStatus.finished_operations}}</div>
            <div><strong>Errored or skipped</strong> {{syncStatus.errored_operations}}</div>
            <div v-if='syncStatus.response_body_url'>
              <a :href="syncStatus.response_body_url" target="_blank" >Download results</a>
            </div>
          </blockquote>
          <v-btn style='float:right;' class='pr-3' small flat pull-right @click='getSyncStatus' >Refresh status</v-btn>
        </v-flex>
        <v-flex v-else xs12 sm6 >
          <v-alert :value='true' outline outlined text type='warning' >
            <strong>Before you sync your mailing list</strong>
            <ol>
              <li>
                If relevant: make sure any automations in Mailchimp are disabled (assuming you don't want to run them when new contacts are added)
              </li>
              <li>
                If you have a very large contact list in AppointmentGuru, consider limiting your
                sync to only more recently active users
              </li>
              <li>
                This sync will match users on e-mail address:
                <ol>
                  <li>If they exist, where relevant, their details will be updated - but they will not be duplicated</li>
                  <li>If the email does not exist, a new member will be added in Mailchimp</li>
                  <li>We also add some merge tags with appointment information (last appointment, next appointment, number of appointments etc.)
                    - you can use this to segment members in mailchimp</li>
                </ol>
              </li>
            </ol>
          </v-alert>
        </v-flex>
      </v-layout>
    </v-card-text>
    <!-- <v-card-actions >
      <v-spacer></v-spacer>
      <v-btn flat text >Done</v-btn>
    </v-card-actions> -->
  </v-card>
  <v-card class='mt-3' v-if='savedToken && savedListId' >
    <v-card-title class='subheading'>{{savedList.name}}
      <v-spacer></v-spacer>
      <v-btn :href='savedList.subscribe_url_long' target='_blank' small flat text >
        Subscription URL
        <v-icon small >open_in_new</v-icon>
      </v-btn>
      <v-btn v-if='openInMailchimp'
        :href='openInMailchimp'
        target='_blank' small flat text >
        Open in Mailchimp
        <v-icon small >open_in_new</v-icon>
      </v-btn>
      <v-btn small color='primary' >Sync</v-btn>
    </v-card-title>
    <v-divider></v-divider>
    <v-card-text v-if='savedList.stats' >
      <v-subheader>Stats</v-subheader>
      <v-list dense>
        <v-list-tile v-for='(value, key) in savedList.stats' :key='key' >
          <v-list-tile-content>
            <v-list-tile-title v-if='key' >{{ key.replace('_', ' ') }}
            </v-list-tile-title>
            <v-list-tile-title v-if='key === "last_sub_date"' v-text='value'></v-list-tile-title>
            <v-list-tile-title v-if='key === "last_unsub_date"' v-text='value'></v-list-tile-title>
          </v-list-tile-content>
          <v-list-tile-avatar v-if='key && !key.endsWith("_date")' >
              {{value || '-'}}
          </v-list-tile-avatar>
        </v-list-tile>
      </v-list>
    </v-card-text>
  </v-card>
  <v-card v-if='savedToken' class='mt-3' >
    <v-card-title class='overline' >
      Danger Zone
    </v-card-title>
    <v-dialog :width='290' v-model='showuninstall' >
      <v-card>
        <v-card-title class='overline' >
          Are you sure you want to unintsall the Mailchimp integration?
        </v-card-title>
        <v-card-text>
        </v-card-text>
        <v-card-actions >
          <v-btn @click='showuninstall=false' flat text >Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn @click='uninstall' :loading='requests.delete.loading' color='error' >
            Uninstall Mailchimp
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-card-actions>
      <v-btn @click='showuninstall=true' :loading='requests.delete.loading' color='error' >
        Uninstall integration</v-btn>
    </v-card-actions>
  </v-card>
</v-form>
</template>
<script>
import Vue from 'vue'
export default {
  name: 'MailchimpIntegrationConfiguration',
  data () {
    return {
      interval: null,
      token: null,
      listId: null,
      showuninstall: false,
      datepicker: false,
      syncSince: null,
      requests: {
        configure: {
          resource: 'ag.integrations',
          extra: 'mailchimp/configure'
        },
        delete: {
          resource: 'ag.integrations',
          extra: 'mailchimp/delete'
        },
        lists: {
          resource: 'ag.integrations',
          extra: 'mailchimp/lists'
        },
        list: {
          resource: 'ag.integrations',
          extra: 'mailchimp/lists'
        },
        sync: {
          resource: 'ag.integrations',
          extra: 'mailchimp/sync'
        },
        syncstatus: {
          resource: 'ag.integrations',
          extra: 'mailchimp/sync'
        }
      }
    }
  },
  mounted () {
    this.get()
  },
  methods: {
    get () {
      this.$request('get', this.requests.configure)
    },
    uninstall () {
      this.$request('delete', this.requests.delete)
    },
    configure () {
      if (this.token) {
        this.requests.configure.data = {
          auth_token: this.token
        }
        this.$request('save', this.requests.configure)
      }
    },
    getLists () {
      this.$request('get', this.requests.lists)
    },
    setList () {
      if (this.listId) {
        this.requests.list.data = {
          list_id: this.listId
        }
        this.$request('save', this.requests.list)
      }
    },
    getSyncStatus () {
      this.requests.syncstatus.params = {
        batch_id: this.syncBatchId
      }
      this.$request('get', this.requests.syncstatus)
    },
    performSync () {
      this.requests.sync.params = {}
      if (this.syncSince) {
        this.requests.sync.data = {
          since: this.syncSince
        }
      } else {
        this.requests.sync.data = {}
      }
      this.$request('save', this.requests.sync)
    }
  },
  watch: {
    syncSince () {
      this.datepicker = false
    },
    savedToken () {
      if (this.savedToken) {
        this.token = this.savedToken
        this.getLists()
        this.getLookups()
      }
    },
    savedListId () {
      this.listId = this.savedListId
      this.getLookups()
    },
    syncBatchId () {
      if (this.syncBatchId) {
        this.interval = setInterval(() => {
          this.getSyncStatus()
          this.requests.syncstatus.loading = true
        }, 4000)
      }
    },
    batchStatus () {
      console.log(this.batchStatus)
      if (this.batchStatus === 'finished') {
        window.clearInterval(this.interval)
        this.getLists()
      }
    },
    listStatus () {
      if (this.listStatus === 200) {
        this.get()
      }
    },
    deleteStatus () {
      if (this.deleteStatus === 204) {
        Vue.set(this.requests, 'configure.result', {})
        this.get()
        this.showuninstall = false
        this.token = null
        this.listId = null
      }
    }
  },
  computed: {
    savedToken () {
      return this.getObjectPath(this.requests, 'configure.result.token', null)
    },
    savedListId () {
      return this.getObjectPath(this.requests, 'configure.result.settings.list_id', null)
    },
    openInMailchimp () {
      if (this.savedToken && this.savedListId) {
        let ds = this.savedToken.split('-')[1]
        return `https://${ds}.admin.mailchimp.com/lists/members?id=${this.savedListId}`
      }
      return null
    },
    syncBatchId () {
      return this.getObjectPath(this.requests, 'sync.result.id', null)
    },
    savedList () {
      if (this.savedListId) {
        let list = this.lists.find((item) => {
          return (item.id === this.savedListId)
        })
        if (!list) { return {} }
        return list
      }
      return {}
    },
    syncStatus () {
      return this.getObjectPath(this.requests, 'syncstatus.result', {})
    },
    batchStatus () {
      return this.getObjectPath(this.requests, 'syncstatus.result.status', {})
    },
    lists () {
      return this.getObjectPath(this.requests, 'lists.result.lists', [])
    },
    list () {
      return this.getObjectPath(this.requests, 'lists.result.list', [])
    },
    listStatus () {
      return this.getObjectPath(this.requests, 'list.status', 0)
    },
    deleteStatus () {
      return this.getObjectPath(this.requests, 'delete.status', 0)
    }
  }
}
</script>



// WEBPACK FOOTER //
// index.vue?22a7f779